import { motion } from 'framer-motion'
import { Lightbulb, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { AISummary, CompetitorResult } from '../../types'
import { APP_NAME, BRAND, CONSULTATION_URL } from '../../config'
import { formatNumber } from '../../lib/utils'

// On mobile/tablet the cards grow to fit content (no text clipping); the fixed
// Figma height only applies from lg up, where the two cards sit side by side.
const CARD_HEIGHT = 'min-h-[338px] lg:h-[338px]'
const CARD_GAP = 'gap-6 lg:gap-[41px]'

// ─── Competitor row ───────────────────────────────────────────────────────────

function CompetitorRow({ comp, rank }: { comp: CompetitorResult; rank: number }) {
  const { t } = useTranslation('report')

  return (
    <div className="flex min-h-[61px] items-center gap-3 rounded-[14px] bg-[#f2f2f6] px-3 py-3">
      <div
        className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
        style={{ backgroundColor: BRAND.navy }}
      >
        {rank}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium leading-5 text-[#1e2939]" dir="auto">
          {comp.name}
        </p>
        <p className="truncate text-xs leading-4 text-[#99a1af]" dir="auto">
          {comp.address || comp.city}
        </p>
      </div>
      <div className="shrink-0 text-end">
        <p className="text-sm font-semibold leading-5 text-[#d08700] tabular-nums">
          ⭐ {comp.rating.toFixed(1)}
        </p>
        <p className="text-xs leading-4 text-[#99a1af]">
          {formatNumber(comp.review_count)} {t('reviews.total')}
        </p>
      </div>
    </div>
  )
}

// ─── Competitors card ─────────────────────────────────────────────────────────

function CompetitorsCard({ competitors, fixedHeight }: { competitors: CompetitorResult[]; fixedHeight?: boolean }) {
  const { t } = useTranslation('report')
  const titleKey = competitors.length === 1 ? 'competitors.title_one' : 'competitors.title_other'

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className={`flex w-full flex-col rounded-2xl bg-white px-[21px] pb-[21px] pt-[27px] ${fixedHeight ? CARD_HEIGHT : 'min-h-[338px]'}`}
    >
      <div className="mb-5 flex shrink-0 items-center gap-2">
        <Users className="h-4 w-4 text-[#99a1af]" />
        <h3 className="text-sm font-bold leading-5 text-[#364153]">
          {t(titleKey, { count: competitors.length })}
        </h3>
      </div>

      <div className="report-scroll-list max-h-[17.5rem] space-y-[15px] overflow-y-auto pe-1">
        {competitors.map((comp, i) => (
          <CompetitorRow key={comp.external_id || String(i)} comp={comp} rank={i + 1} />
        ))}
      </div>
    </motion.div>
  )
}

// ─── AI Summary card ──────────────────────────────────────────────────────────

function AISummaryCard({ summary, fixedHeight }: { summary: AISummary; fixedHeight?: boolean }) {
  const { t } = useTranslation('report')

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className={`flex w-full flex-col gap-5 overflow-hidden rounded-2xl border-s-4 border-s-[#fd9a00] bg-white px-[21px] pb-[21px] pt-[27px] ${fixedHeight ? CARD_HEIGHT : 'min-h-[338px]'}`}
    >
      <div className="flex shrink-0 items-center gap-2">
        <Lightbulb className="h-4 w-4 text-amber-500" />
        <h3 className="text-sm font-bold leading-5 text-[#01114f]">{t('ai.title')}</h3>
        {summary.is_fallback && (
          <span className="rounded-full bg-[#f2f2f6] px-2 py-0.5 text-xs text-[#99a1af]">
            {t('ai.template')}
          </span>
        )}
      </div>

      <div className="min-h-0 flex-1">
        <p className="text-sm leading-relaxed text-[#4a5565]" dir="auto">
          {summary.executive_summary}
        </p>
      </div>

      <div className="shrink-0 rounded-[14px] bg-[#f2f2f6] px-5 py-4">
          <p className="mb-1.5 text-xs font-bold text-[#01114f]">
            {t('sectionCallout.title', { appName: APP_NAME })}
          </p>
          <p className="mb-2 text-[10px] leading-4 text-[#4a5565]">
            {summary.competitor_gap || t('callout.competitors.message', { appName: APP_NAME })}
          </p>
          <a
            href={CONSULTATION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-bold text-[#ef042d] transition-opacity hover:opacity-90"
          >
            {t('callout.competitors.cta')}
          </a>
        </div>
    </motion.div>
  )
}

// ─── Two-column overview row ──────────────────────────────────────────────────

interface ReportOverviewGridProps {
  aiSummary: AISummary | null
  competitors: CompetitorResult[]
}

export function ReportOverviewGrid({ aiSummary, competitors }: ReportOverviewGridProps) {
  const hasAi = !!aiSummary
  const hasCompetitors = competitors.length > 0
  if (!hasAi && !hasCompetitors) return null

  if (hasAi && hasCompetitors) {
    return (
      <div className={`mb-8 grid grid-cols-1 ${CARD_GAP} lg:grid-cols-2`}>
        <AISummaryCard summary={aiSummary} fixedHeight />
        <CompetitorsCard competitors={competitors} fixedHeight />
      </div>
    )
  }

  if (hasAi) {
    return (
      <div className="mb-8">
        <AISummaryCard summary={aiSummary} />
      </div>
    )
  }

  return (
    <div className="mb-8 max-w-md">
      <CompetitorsCard competitors={competitors} />
    </div>
  )
}
